<template>
    <b-sidebar
        v-model="visible"
        right
        shadow
        width="720px"
        backdrop
        no-header
        sidebar-class="stl-settlement-portal"
        @hidden="onHidden"
    >
        <div class="stl-drawer-wrap settlement-page" tabindex="-1" @keydown.esc.stop="closeDrawer">
            <div class="stl-drawer-head">
                <button type="button" class="stl-drawer-close" aria-label="닫기" @click="closeDrawer">×</button>
                <span class="stl-drawer-title">정산 실행</span>
                <div class="stl-drawer-context">
                    <span class="stl-drawer-site">{{ site ? site.siteName : "" }}</span>
                    <span class="stl-drawer-sep">·</span>
                    <span class="stl-drawer-energy">{{ energyLabelText }}</span>
                </div>
                <span class="stl-drawer-sp"></span>
                <span
                    v-if="settlementStatus"
                    class="stl-drawer-status-badge"
                    :class="settlementStatus.toLowerCase()"
                    >{{ statusBadgeLabel }}</span
                >
            </div>
            <div class="stl-drawer-body">
                <SettlementRunBundle
                    v-if="visible && site && isBundleMode"
                    :site="site"
                    :ym="ym"
                    @settled="$emit('settled')"
                    @status-change="settlementStatus = $event"
                />
                <SettlementRunTab
                    v-else-if="visible && site"
                    :site="site"
                    :ym="ym"
                    :energy-source="energySource"
                    @settled="$emit('settled')"
                    @open-site-settings="$emit('open-site-settings')"
                    @status-change="settlementStatus = $event"
                />
            </div>
        </div>
    </b-sidebar>
</template>

<script>
import SettlementRunTab from "../tabs/SettlementRunTab.vue";
import SettlementRunBundle from "./SettlementRunBundle.vue";
import { statusLabel } from "../utils/settlementFormat.js";

export default {
    name: "SettlementRunDrawer",
    components: { SettlementRunTab, SettlementRunBundle },
    props: {
        value: { type: Boolean, default: false },
        site: { type: Object, default: null },
        ym: { type: String, required: true },
        energySource: { type: String, default: "Elec" },
    },
    data() {
        return {
            settlementStatus: null,
        };
    },
    computed: {
        visible: {
            get() {
                return this.value;
            },
            set(v) {
                this.$emit("input", v);
            },
        },
        energyLabelText() {
            if (this.isBundleMode) return "전체";
            if (this.energySource === "Gas") return "가스";
            if (this.energySource === "Water") return "수도";
            return "전기";
        },
        isBundleMode() {
            return this.energySource === "ALL";
        },
        statusBadgeLabel() {
            return statusLabel(this.settlementStatus) || this.settlementStatus;
        },
    },
    watch: {
        visible(open) {
            if (open) {
                this.settlementStatus = null;
                this.$nextTick(() => {
                    document.addEventListener("keydown", this.onDocEsc, true);
                    const wrap = this.$el?.querySelector?.(".stl-drawer-wrap");
                    wrap?.focus?.();
                });
            } else {
                document.removeEventListener("keydown", this.onDocEsc, true);
            }
        },
    },
    beforeDestroy() {
        document.removeEventListener("keydown", this.onDocEsc, true);
    },
    methods: {
        closeDrawer() {
            this.visible = false;
        },
        onHidden() {
            document.removeEventListener("keydown", this.onDocEsc, true);
            this.$emit("close");
        },
        onDocEsc(e) {
            if (e.key !== "Escape" && e.code !== "Escape") return;
            if (!this.visible) return;
            e.preventDefault();
            e.stopPropagation();
            this.closeDrawer();
        },
    },
};
</script>

<style scoped>
.stl-drawer-wrap {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    outline: none;
}
.stl-drawer-head {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 20px;
    border-bottom: 1px solid #e6e9ef;
    flex-shrink: 0;
    background: #fff;
}
.stl-drawer-close {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    border: 1px solid #e6e9ef;
    background: #fff;
    color: #7e8798;
    font-size: 17px;
    line-height: 1;
    cursor: pointer;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}
.stl-drawer-close:hover {
    background: #f7f8fb;
}
.stl-drawer-title {
    font-size: 16px;
    font-weight: 700;
    color: #1a1f27;
    white-space: nowrap;
    flex-shrink: 0;
    letter-spacing: -0.01em;
}
.stl-drawer-context {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    overflow: hidden;
}
.stl-drawer-site {
    font-size: 13px;
    font-weight: 600;
    color: #7e8798;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.stl-drawer-sep {
    color: #aab2c0;
    font-size: 12px;
    flex-shrink: 0;
}
.stl-drawer-energy {
    font-size: 13px;
    color: #7e8798;
    white-space: nowrap;
}
.stl-drawer-sp {
    flex: 1;
}
.stl-drawer-status-badge {
    font-size: 11px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 999px;
    border: 1px solid;
    white-space: nowrap;
    flex-shrink: 0;
}
.stl-drawer-status-badge.draft {
    color: #a06d1a;
    background: #fbf5e0;
    border-color: #e8d9a8;
}
.stl-drawer-status-badge.final {
    color: #1e7e52;
    background: #eaf7ef;
    border-color: #b9e2cb;
}
.stl-drawer-status-badge.cancelled {
    color: #aab2c0;
    background: #eef0f5;
    border-color: #d2d8e2;
}
.stl-drawer-status-badge.preview {
    color: #234e9d;
    background: #eff3fb;
    border-color: #bccbea;
}
.stl-drawer-body {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding: 0;
}
</style>
